import React, { useCallback, useMemo } from 'react';

import { useAppSelector, useAuthentication, useBreakpoints, useNewOrder } from '@pkg/hooks';
import { type BaseCurrencyType, type MarketplaceCollectible } from '@pkg/types';

import { type ActiveListingDetails, type CollectionChainType } from '@app/client';
import { useNFTsPageStore, useOrdersStore } from '@app/store/zustand';

import { CurrencyIcon } from './CurrencyIcon';
import { MarketplaceButton } from './MarketplaceButton';
import { NFTManagerControls } from './NFTManagerControls';
import { SelectedButton } from './SelectedButton';

import { Wrapper } from './styles';

interface CardMarketplaceControlsProps {
    nftItem: MarketplaceCollectible;
    isHovering?: boolean;
    chain?: CollectionChainType;
    item_id?: string;
    isOwner?: boolean;
    contractAddress: string;
    tokenId: string;
    listingData?: ActiveListingDetails;
}

export const CardMarketplaceControls = ({
    contractAddress,
    tokenId,
    nftItem,
    chain,
    item_id,
    isOwner,
    isHovering,
    listingData,
}: CardMarketplaceControlsProps) => {
    const { orderRequestPendingId } = useOrdersStore();

    const { protectedAction } = useAuthentication();
    const marketplaceV1Enabled = useAppSelector((state) => state.featureFlags.features.marketplaceV1Enabled);
    const { selectedItems } = useNFTsPageStore();
    const { isMobile } = useBreakpoints();

    const { buy, offer } = useNewOrder();

    const isSelected = selectedItems.includes(item_id || '');

    const isListed = chain ? !!listingData : false;
    const marketplaceItem = useMemo(() => {
        const itemPrice = listingData
            ? {
                  value: listingData?.wei_price || '0',
                  currency: (listingData?.currency_symbol?.toLowerCase() || 'eth') as BaseCurrencyType,
              }
            : nftItem.price;
        return {
            ...nftItem,
            id: nftItem.id ?? `${chain?.toLowerCase()}.${contractAddress}.${tokenId}`,
            price: itemPrice,
        };
    }, [chain, contractAddress, listingData, nftItem, tokenId]);

    const onMarketplaceClick = useCallback(() => {
        return protectedAction(() => {
            if (isListed) {
                return buy([marketplaceItem]);
            } else {
                return offer([marketplaceItem]);
            }
        });
    }, [buy, isListed, marketplaceItem, offer, protectedAction]);

    const marketplaceCTA = marketplaceV1Enabled && (
        <MarketplaceButton
            isListed={isListed}
            onClick={onMarketplaceClick}
            isLoading={Boolean(orderRequestPendingId?.length && orderRequestPendingId === nftItem?.id)}
        />
    );

    return (
        <Wrapper $isHovering={isHovering}>
            {(isHovering || isMobile) && <CurrencyIcon chain={chain} />}
            {(isHovering || isSelected || isMobile) && marketplaceV1Enabled && isOwner && (
                <SelectedButton item_id={item_id} isSelected={isSelected} />
            )}
            {!isOwner ? marketplaceCTA : <NFTManagerControls chain={chain} nftItem={nftItem} isListed={isListed} />}
        </Wrapper>
    );
};
